/**
 * The inverse of the regularized incomplete beta function.
 *
 * The Clopper–Pearson "exact" interval for a proportion is two beta quantiles, and a
 * quantile is the one thing `special.ts` does not provide for the beta. It is found here
 * by bisection on [0, 1] against `regularizedBeta`, which is monotone in x — slow by the
 * standards of a numerical library and immaterial at the handful of calls a confidence
 * interval needs, and it cannot diverge.
 *
 * The stopping rule is relative to the bracket, not absolute: a lower Clopper–Pearson
 * bound for one success in a few thousand trials is around 1e-5, and an absolute
 * tolerance would return it to only a few significant digits.
 */

import { regularizedBeta } from "@/lib/stats/special";

const MAX_ITERATIONS = 2000;
const RELATIVE_TOLERANCE = 1e-15;

/** x such that I_x(a, b) = p, for a, b > 0. */
export function betaQuantile(p: number, a: number, b: number): number {
  if (Number.isNaN(p) || a <= 0 || b <= 0) {
    return Number.NaN;
  }
  if (p <= 0) {
    return 0;
  }
  if (p >= 1) {
    return 1;
  }
  let low = 0;
  let high = 1;
  for (let i = 0; i < MAX_ITERATIONS; i++) {
    const mid = (low + high) / 2;
    if (mid === low || mid === high) {
      break;
    }
    if (regularizedBeta(mid, a, b) < p) {
      low = mid;
    } else {
      high = mid;
    }
    if (high - low <= RELATIVE_TOLERANCE * high) {
      break;
    }
  }
  return (low + high) / 2;
}
